/**
 * Per-character row in the supervisor project overview.
 *
 * One row per character. Columns:
 *   code | display_name | actor | approved/total | pending pickups |
 *   latest stem version | actions
 *
 * Actions are sourced from `useRenderCharacterStem` and the server-supplied
 * download URLs on the stem row. The render and download buttons share the
 * same row so a supervisor can re-render and re-download in two clicks.
 */
import { Badge, Button, ProgressBar } from '@ohear/ui'
import { useProjectStems, useRenderCharacterStem } from '../../lib/api/stems'
import type { CharacterStem } from '../../lib/api/stems'
import type { SupervisorCharacterRow } from '../../lib/api/supervisor'

export interface CharacterTableProps {
  projectId: string
  characters: readonly SupervisorCharacterRow[]
}

export function CharacterTable({ projectId, characters }: CharacterTableProps) {
  const stems = useProjectStems(projectId)
  const stemByCharacter = new Map<string, CharacterStem>()
  for (const s of stems.data?.characters ?? []) {
    stemByCharacter.set(s.character_id, s)
  }

  return (
    <div className="overflow-x-auto rounded-[var(--radius)] border border-[var(--border)]">
      <table
        className="w-full text-[13px] border-separate"
        style={{ borderSpacing: 0 }}
        role="table"
        aria-label="Characters"
      >
        <thead
          className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]"
          style={{ background: 'var(--bg-3)' }}
        >
          <tr>
            <Th>Code</Th>
            <Th>Character</Th>
            <Th>Actor</Th>
            <Th>Progress</Th>
            <Th>Pickups</Th>
            <Th>Stem</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {characters.length === 0 ? (
            <tr>
              <td
                colSpan={7}
                className="px-3 py-6 text-center text-[var(--text-2)] border-t"
                style={{ borderColor: 'var(--border)' }}
              >
                No characters yet.
              </td>
            </tr>
          ) : null}
          {characters.map((c) => (
            <CharacterRow
              key={c.character_id}
              projectId={projectId}
              character={c}
              stem={stemByCharacter.get(c.character_id) ?? null}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CharacterRow({
  projectId,
  character,
  stem,
}: {
  projectId: string
  character: SupervisorCharacterRow
  stem: CharacterStem | null
}) {
  const render = useRenderCharacterStem(projectId)
  const pct = character.total === 0 ? 0 : Math.round((character.approved / character.total) * 100)
  const stemStatus = stem?.status ?? 'idle'
  const ready = stemStatus === 'ready'
  const busy = stemStatus === 'queued' || stemStatus === 'rendering' || render.isPending

  return (
    <tr className="align-middle transition-colors hover:bg-[var(--bg-3)]">
      <Td>
        <code className="font-mono text-[12px] text-[var(--text-2)]">
          {character.character_code}
        </code>
      </Td>
      <Td>{character.character_display_name}</Td>
      <Td>
        {character.actor_display_name ? (
          character.actor_display_name
        ) : (
          <span className="text-[var(--text-3)]">unassigned</span>
        )}
      </Td>
      <Td>
        <div className="flex items-center gap-2 min-w-[140px]">
          <ProgressBar
            value={pct}
            size="sm"
            tone={pct === 100 ? 'green' : 'accent'}
            label="Approved"
          />
          <span className="text-[var(--text-2)] tabular-nums whitespace-nowrap">
            {character.approved}/{character.total}
          </span>
        </div>
      </Td>
      <Td>
        {character.pending_pickups > 0 ? (
          <Badge variant="amber">{character.pending_pickups}</Badge>
        ) : (
          <span className="text-[var(--text-3)]">-</span>
        )}
      </Td>
      <Td>
        <div className="inline-flex items-center gap-2">
          {character.latest_stem_version ? (
            <Badge variant="blue">v{character.latest_stem_version}</Badge>
          ) : (
            <span className="text-[var(--text-3)]">-</span>
          )}
          {stemStatus === 'rendering' || stemStatus === 'queued' ? (
            <Badge variant="amber">{stemStatus}</Badge>
          ) : null}
          {stemStatus === 'failed' ? <Badge variant="red">failed</Badge> : null}
        </div>
      </Td>
      <Td>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            disabled={!ready || !stem?.download_url}
            onClick={() => {
              if (stem?.download_url) window.open(stem.download_url, '_blank', 'noopener')
            }}
          >
            WAV
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={!stem?.qc_pdf_url}
            onClick={() => {
              if (stem?.qc_pdf_url) window.open(stem.qc_pdf_url, '_blank', 'noopener')
            }}
          >
            QC PDF
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            disabled={busy || character.approved === 0}
            onClick={() => {
              render.mutate(character.character_id)
            }}
          >
            {character.latest_stem_version ? 'Re-render' : 'Render'}
          </Button>
        </div>
      </Td>
    </tr>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td className="px-3 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
      {children}
    </td>
  )
}
